// @ts-strict-ignore
"use client";
import { useEffect, useRef, useState } from "react";
import { animate } from "framer-motion";
import debounce from "lodash/debounce";
import { useWidthObserver } from "hooks/useWidthObserver";

export const enum LoadingState {
  LOADING = "LOADING",
  DEFAULT = "DEFAULT",
  ERROR = "ERROR"
}

export const DIRECTION = {
  back: "back",
  next: "next"
} as const;

export function useHorizontalScroll(
  cardWidth: number,
  gapSize: number,
  slidesToScroll?: number,
  loadingState?: LoadingState
) {
  const [isBackDisabled, setIsBackDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const { windowWidth: width } = useWidthObserver();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // update back/next when the data comes back in.
    if (loadingState === LoadingState.DEFAULT && ref.current) {
      const isScrollHidden = ref.current.scrollWidth <= ref.current.clientWidth;
      setIsNextDisabled(isScrollHidden);
    } else if (ref.current) {
      const isScrollHidden = ref.current.scrollWidth <= ref.current.clientWidth;
      setIsNextDisabled(isScrollHidden);
    }
  }, [width, ref?.current?.scrollWidth, loadingState]);

  // only animate on click
  const handleClick = (direction: keyof typeof DIRECTION) => {
    const animateWidth = (cardWidth + gapSize) * (slidesToScroll ?? 1);
    // single card animate
    const scrollToValue =
      direction === DIRECTION.next
        ? ref.current.scrollLeft + animateWidth
        : ref.current.scrollLeft - animateWidth;
    animate(ref.current.scrollLeft, scrollToValue, {
      onUpdate: (value) => (ref.current.scrollLeft = value),
      duration: 0.4
    });
  };

  const getCurrentScrollPercent = (container: HTMLDivElement) => {
    if (container) {
      const currentScrollPos = container.scrollLeft;

      const currentScrollPercent =
        (currentScrollPos / (container.scrollWidth - container.clientWidth)) *
        100;
      return currentScrollPercent;
    } else {
      return 0;
    }
  };

  // animate will trigger this as well so keep page logic here to also handle manually scrolling
  const handleScroll = debounce(() => {
    const currentScrollPercent = getCurrentScrollPercent(ref.current);
    if (currentScrollPercent < 1) {
      setIsBackDisabled(true);
      setIsNextDisabled(false);
    } else if (currentScrollPercent > 99) {
      setIsNextDisabled(true);
      setIsBackDisabled(false);
    } else {
      setIsNextDisabled(false);
      setIsBackDisabled(false);
    }
  }, 150);
  return { ref, handleClick, handleScroll, isBackDisabled, isNextDisabled };
}
